// js/Api/address_book.js
//
// Address Book API — see docs/API spec for endpoint contracts.
// Base path: ${REACT_APP_API_ORIGIN}/portaladdressbook
//
// NOTE: Entry-level operations (fetch / create / update / delete entries) are
// NOT org-specific. Once you have an address_book_id the same endpoints are
// used regardless of whether the book is owned by a device or an org. The
// org-prefixed entry endpoints on the backend are redundant and intentionally
// not used here.

import {
    apiGet,
    apiPost,
    apiPut,
    apiDelete,
    apiPostMultipart,
} from "./api_call/api_methods";

const BASE = `${process.env.REACT_APP_API_ORIGIN}/portaladdressbook`;

// ---------------------------------------------------------------------------
// Device address books
// ---------------------------------------------------------------------------

export const fetchAddressBooksForDevice = async (deviceId) => {
    if (!deviceId) {
        return { success: false, error: "Device ID is required" };
    }
    return apiGet({
        endpoint: `${BASE}/device/${deviceId}`,
        error_message: "Failed to fetch address books",
    });
};

// ---------------------------------------------------------------------------
// Entries (shared by device + org books — keyed only by address_book_id / entry_id)
// ---------------------------------------------------------------------------

export const fetchAddressBookEntries = async (
    addressBookId,
    { updatedSince, includeDeleted } = {}
) => {
    if (!addressBookId) {
        return { success: false, error: "Address book ID is required" };
    }
    const params = new URLSearchParams();
    if (updatedSince) params.set("updated_since", updatedSince);
    if (includeDeleted) params.set("include_deleted", "true");
    const qs = params.toString();
    return apiGet({
        endpoint: `${BASE}/${addressBookId}/entries${qs ? `?${qs}` : ""}`,
        error_message: "Failed to fetch address book entries",
    });
};

export const createPhoneContact = async (
    addressBookId,
    { displayName, phoneNumberRaw, callMethod = "sip_audio", classification = "safe", file }
) => {
    if (!addressBookId) {
        return { success: false, error: "Address book ID is required" };
    }
    const formData = new FormData();
    formData.append("displayName", displayName ?? "");
    formData.append("phoneNumberRaw", phoneNumberRaw ?? "");
    formData.append("callMethod", callMethod);
    formData.append("classification", classification || "safe");
    if (file) {
        formData.append("file", file);
    }
    return apiPostMultipart({
        endpoint: `${BASE}/${addressBookId}/entries`,
        formData,
        successMessage: "Contact added",
        errorMessage: "Failed to add contact",
    });
};

export const updateAddressBookEntry = async (entryId, body) => {
    if (!entryId) {
        return { success: false, error: "Entry ID is required" };
    }
    return apiPut({
        endpoint: `${BASE}/entries/${entryId}`,
        body,
        successMessage: "Contact updated",
        errorMessage: "Failed to update contact",
    });
};

export const deleteAddressBookEntry = async (entryId) => {
    if (!entryId) {
        return { success: false, error: "Entry ID is required" };
    }
    return apiDelete({
        endpoint: `${BASE}/entries/${entryId}`,
        successMessage: "Contact deleted",
        errorMessage: "Failed to delete contact",
    });
};

export const uploadEntryAvatar = async (entryId, file) => {
    if (!entryId) {
        return { success: false, error: "Entry ID is required" };
    }
    if (!file) {
        return { success: false, error: "File is required" };
    }
    const formData = new FormData();
    formData.append("file", file);
    return apiPostMultipart({
        endpoint: `${BASE}/entries/${entryId}/avatar`,
        formData,
        successMessage: "Avatar updated",
        errorMessage: "Failed to upload avatar",
    });
};

// ---------------------------------------------------------------------------
// Organization address books (book-level CRUD)
// ---------------------------------------------------------------------------

/** Fetch all address books for an organisation. */
export const fetchOrgAddressBooks = async (orgId) => {
    if (!orgId) return { success: false, error: "Organisation ID is required" };
    return apiGet({
        endpoint: `${BASE}/org/${orgId}`,
        error_message: "Failed to fetch address books",
    });
};

/** Create a new address book for an organisation. */
export const createOrgAddressBook = async (
    orgId,
    { name, classification = "safe" }
) => {
    if (!orgId) return { success: false, error: "Organisation ID is required" };
    const trimmed = (name || "").trim();
    if (!trimmed) return { success: false, error: "Name is required" };
    return apiPost({
        endpoint: `${BASE}/org/${orgId}`,
        body: { name: trimmed, classification },
        successMessage: "Address book created",
        errorMessage: "Failed to create address book",
    });
};

/**
 * Update an org address book's name / classification.
 * Backend route is PUT /org/{orgId} with the book id in the body, so this
 * needs the orgId AND the book id.
 */
export const updateOrgAddressBook = async (
    orgId,
    { id, name, classification = "safe" }
) => {
    if (!orgId) return { success: false, error: "Organisation ID is required" };
    if (!id) return { success: false, error: "Book ID is required" };
    const trimmed = (name || "").trim();
    if (!trimmed) return { success: false, error: "Name is required" };
    return apiPut({
        endpoint: `${BASE}/org/${orgId}`,
        body: { id, name: trimmed, classification },
        successMessage: "Address book updated",
        errorMessage: "Failed to update address book",
    });
};

/**
 * Delete an org address book.
 * NOTE: backend DELETE endpoint not implemented yet — to be added as
 * DELETE /portaladdressbook/org/{address_book_id}.
 */
export const deleteOrgAddressBook = async (addressBookId) => {
    if (!addressBookId) return { success: false, error: "Book ID is required" };
    return apiDelete({
        endpoint: `${BASE}/org/${addressBookId}`,
        successMessage: "Address book deleted",
        errorMessage: "Failed to delete address book",
    });
};

// ---------------------------------------------------------------------------
// Organization entries — reuse the shared entry endpoints above
// ---------------------------------------------------------------------------

/** Fetch all entries for an org book (same endpoint as device books). */
export const fetchOrgAddressBookEntries = async (bookId) =>
    fetchAddressBookEntries(bookId);

/** Create a phone contact in an org book. */
export const createOrgPhoneContact = async (bookId, payload) =>
    createPhoneContact(bookId, payload);

/** Update an org book entry. */
export const updateOrgAddressBookEntry = async (entryId, body) =>
    updateAddressBookEntry(entryId, body);

/** Soft-delete an org book entry. */
export const deleteOrgAddressBookEntry = async (entryId) =>
    deleteAddressBookEntry(entryId);

// ---------------------------------------------------------------------------
// Share & copy
// ---------------------------------------------------------------------------

/**
 * Share an address book with devices and/or device (policy) groups.
 * Pass deviceIds and/or policyGroupIds — at least one selection is required.
 * NOTE: backend currently only persists policyGroupIds (group shares).
 * deviceIds are sent ahead of the backend device-share support being added.
 */
export const shareAddressBook = async ({
    addressBookId,
    deviceIds = [],
    policyGroupIds = [],
    permission = "read_only",
}) => {
    if (!addressBookId) {
        return { success: false, error: "Address book ID is required" };
    }
    const devs = Array.isArray(deviceIds) ? deviceIds : [];
    const groups = Array.isArray(policyGroupIds) ? policyGroupIds : [];
    if (devs.length === 0 && groups.length === 0) {
        return { success: false, error: "Select at least one device or device group" };
    }
    return apiPost({
        endpoint: `${BASE}/org/share`,
        body: {
            addressBookId,
            deviceIds: devs,
            policyGroupIds: groups,
            permission,
        },
        successMessage: "Address book shared",
        errorMessage: "Failed to share address book",
    });
};

/** Get the existing shares for an address book. */
export const getAddressBookShares = async (addressBookId) => {
    if (!addressBookId) {
        return { success: false, error: "Address book ID is required" };
    }
    return apiGet({
        endpoint: `${BASE}/org/${addressBookId}/shares`,
        error_message: "Failed to fetch address book shares",
    });
};

/**
 * Copy selected entries into a target book.
 * Accepts either an array of entry objects or an array of entry ids.
 */
export const copyOrgEntriesToBook = async (targetBookId, entries) => {
    if (!targetBookId) {
        return { success: false, error: "Target book ID is required" };
    }
    if (!Array.isArray(entries) || entries.length === 0) {
        return { success: false, error: "No entries selected" };
    }
    const entryIds = entries.map((e) => (typeof e === "string" ? e : e.id));
    return apiPost({
        endpoint: `${BASE}/org/copy-entries`,
        body: { targetAddressBookId: targetBookId, entryIds },
        successMessage: "Contacts copied",
        errorMessage: "Failed to copy contacts",
    });
};
